import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';

import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { BudgetChapterGridObject, BudgetChapterObject } from '@libs/types/src/mado/object-type/organization';
import { budgetChapterFrag, budgetChapterMutationFrag } from './organization-settings-budget-chapter-dialog.query';
import { OrganizationSettingBudgetChapterDialogStore } from './organization-settings-budget-chapter-dialog.store';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { FilterBuilder } from 'packages/client/src/app/modules/content/helper/filter.builder';

@Component({
    selector: 'app-organization-settings-budget-chapter-dialog',
    templateUrl: './organization-settings-budget-chapter-dialog.component.html',
    providers: [OrganizationSettingBudgetChapterDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextInputComponent,
        SelectInputComponent,
    ]
})
export class OrganizationSettingsBudgetChapterDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationSettingBudgetChapterDialogStore);

    protected readonly data = this.dialogConfig.data.budgetChapter || null;

    budgetChapterOptions = signal<BudgetChapterGridObject[]>([])

    protected form = fb.group({
        id: fb.control<number | null>(null),
        parentId: fb.control<number | null>(null),
        name: fb.control<string | null>(null, [Validators.required]),
        code: fb.control<string | null>(null, [Validators.required]),
        description: fb.control<string | null>(null),
    })

    protected readonly prefix = 'organization-settings.mado.budgetChapter.';

    constructor() {
        super();
        this.patchForm(this.data);

        effect(() => {
            this.loadBudgetChapters();
        })
    }

    override async save() {
        if (isInvalid(this.form)) return;
        
        const data = {
            parentId: this.form.value.parentId,
            name: this.form.value.name!,
            code: this.form.value.code!,
            description: this.form.value.description,
        }

        if (this.form.value.id) {
            await this.store.api.updateBudgetChapter(
                {
                    id: this.form.value.id,
                    ...data,
                },
                budgetChapterMutationFrag,
            )
            contentGlobalSuccess.next('common.updated')
            this.dialogRef.close(true);

        } else if (routeParams.organizationId()) {
            await this.store.api.createBudgetChapter(
                {
                    organizationId: Number(routeParams.organizationId()),
                    ...data,
                },
                budgetChapterMutationFrag,
            )
            contentGlobalSuccess.next('common.created')
            this.dialogRef.close(true);
        }
    }

    private patchForm(item: BudgetChapterObject | null) {
        if (item) {
            this.form.patchValue({
                id: item.id,
                parentId: item.parentId,
                name: item.name,
                code: item.code,
                description: item.description,
            })
        }
    }

    async loadBudgetChapters() {
        const organizationId = routeParams.organizationId();
        
        const response = await this.store.api.getBudgetChapters(FilterBuilder.singlePage({ organizationId }), budgetChapterFrag);

        if (response.items) {
            if (this.data)
                this.budgetChapterOptions.set(response.items.filter((i: BudgetChapterGridObject) => i.id !== this.data.id));
            else
                this.budgetChapterOptions.set(response.items)
        }   
    }
}
